import React, { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, fileUrl } from "@/lib/api";
import { fmtEuro, fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, Trash2, Search, ChefHat, Upload, X, Clock } from "lucide-react";
import { SubLinks } from "@/components/SubLinks";

const EMPTY = {
  name: "", category: "Κυρίως", portions: 1, menu_price: "", directions: [""],
  items: [], photo_file_id: null, allergens: [], prep_minutes: "", notes: "", active: true,
};

const UNITS = ["gr", "kg", "ml", "lt", "τεμ"];

function CostBadge({ costing, canSeeCost }) {
  if (!canSeeCost || !costing) return null;
  const fc = costing.food_cost_pct;
  const tone = fc === null ? "bg-[#F1EDE6] text-[#45423C]" : fc <= 30 ? "bg-[#EDF2E8] text-[#4A5A3E]" : fc <= 38 ? "bg-[#FBF3E4] text-[#8A6A22]" : "bg-[#FEF2F2] text-[#991B1B]";
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="rounded-full bg-[#F1EDE6] px-2.5 py-1 font-semibold tabular">{fmtEuroCents(costing.cost_per_portion)} / μερίδα</span>
      <span className={`rounded-full px-2.5 py-1 font-semibold ${tone}`}>{fc === null ? "χωρίς τιμή μενού" : `food cost ${fc}%`}</span>
      {costing.incomplete && <span className="rounded-full bg-[#FEF2F2] px-2.5 py-1 font-semibold text-[#991B1B]">ελλιπές κόστος</span>}
    </div>
  );
}

export default function Recipes() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const photoRef = useRef(null);

  const { data: meta } = useQuery({ queryKey: ["recipes-meta"], queryFn: async () => (await api.get("/recipes/meta")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["recipes", q, cat],
    queryFn: async () => (await api.get(`/recipes?q=${encodeURIComponent(q)}&category=${encodeURIComponent(cat)}`)).data,
  });
  const { data: ings } = useQuery({
    queryKey: ["ingredients", ""],
    queryFn: async () => (await api.get("/recipes/ingredients")).data,
    enabled: !!meta?.can_edit,
  });

  const canSeeCost = !!data?.can_see_cost;
  const canEdit = !!meta?.can_edit;
  const ingMap = useMemo(() => Object.fromEntries((ings?.rows || []).map((i) => [i.id, i])), [ings]);

  const save = useMutation({
    mutationFn: async (r) => {
      const body = {
        name: r.name, category: r.category, portions: Number(r.portions) || 1,
        menu_price: Number(r.menu_price) || 0,
        directions: (r.directions || []).map((d) => d.trim()).filter(Boolean),
        items: (r.items || []).filter((i) => i.ingredient_id).map((i) => ({
          ingredient_id: i.ingredient_id, qty: Number(i.qty) || 0, unit: i.unit || "gr", note: i.note || "",
        })),
        photo_file_id: r.photo_file_id || null, allergens: r.allergens || [],
        prep_minutes: Number(r.prep_minutes) || 0, notes: r.notes || "", active: r.active !== false,
      };
      return r.id ? (await api.put(`/recipes/${r.id}`, body)).data : (await api.post("/recipes", body)).data;
    },
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["recipes"] }); toast.success("Η συνταγή αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/recipes/${id}`)).data,
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["recipes"] }); toast.success("Η συνταγή διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const uploadPhoto = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/upload", fd)).data;
    },
    onSuccess: (d) => { setEdit((p) => ({ ...p, photo_file_id: d.file_id })); toast.success("Η φωτογραφία ανέβηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const liveCost = (r) => {
    let total = 0;
    (r.items || []).forEach((it) => {
      const ing = ingMap[it.ingredient_id];
      if (!ing) return;
      const factor = { gr: 0.001, kg: 1, ml: 0.001, lt: 1 }[it.unit] ?? 1;
      const pf = { gr: 0.001, kg: 1, ml: 0.001, lt: 1 }[ing.unit] ?? 1;
      const qty = (Number(it.qty) || 0) * factor / pf;
      total += qty * (Number(ing.price_per_unit) || 0) * (1 + (Number(ing.waste_pct) || 0) / 100);
    });
    const per = total / Math.max(1, Number(r.portions) || 1);
    const price = Number(r.menu_price) || 0;
    return { total, per, fc: price ? Math.round((per / price) * 1000) / 10 : null, suggested: per ? per / 0.3 : 0 };
  };

  const field = "mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="recipes-page">
      <SubLinks links={[{ to: "/recipes-bulk", label: "Μαζική Επεξεργασία Συνταγών" }, { to: "/ingredients", label: "Υλικά & Τιμές" }]} />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">F&amp;B · Συνταγολόγιο</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Συνταγές</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Εκτέλεση, υλικά, φωτογραφία παρουσίασης και αυτόματο κόστος από τον τιμοκατάλογο υλικών.</p>
        </div>
        {canEdit && (
          <button data-testid="rec-new" onClick={() => setEdit({ ...EMPTY })}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            <Plus className="h-4 w-4" /> Νέα συνταγή
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="rec-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση συνταγής"
            className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
        </div>
        <select data-testid="rec-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)}
          className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]">
          <option value="all">Όλες οι κατηγορίες</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-sm text-[#6F6A60]">{data?.count || 0} συνταγές</span>
      </div>

      {isLoading ? <div className="p-10 text-center text-[#6F6A60]">Φόρτωση…</div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {(data?.rows || []).map((r) => (
            <div key={r.id} data-testid={`rec-card-${r.id}`}
              className="rounded-xl bg-white border border-[#E3DED4] overflow-hidden hover:border-[#B9AE9A] transition-colors">
              <button onClick={() => setView(r)} className="block w-full text-left">
                <div className="h-20 bg-[#F1EDE6] flex items-center justify-center overflow-hidden">
                  {r.photo_file_id
                    ? <img src={fileUrl(r.photo_file_id)} alt={r.name} className="h-full w-full object-cover" />
                    : <ChefHat className="h-6 w-6 text-[#B9AE9A]" />}
                </div>
                <div className="p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-1.5">
                    <h3 className="font-serif text-sm text-[#45423C] leading-snug">{r.name}</h3>
                    <span className="text-[9px] uppercase tracking-wider text-[#6F6A60] whitespace-nowrap mt-0.5">{r.category}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[#6F6A60]">
                    <span>{r.portions} μερ.</span>
                    {r.prep_minutes ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{r.prep_minutes}′</span> : null}
                    {canSeeCost && r.menu_price ? <span className="tabular">μενού {fmtEuro(r.menu_price)}</span> : null}
                  </div>
                  <CostBadge costing={r.costing} canSeeCost={canSeeCost} />
                </div>
              </button>
              {canEdit && (
                <div className="px-3 pb-3">
                  <button data-testid={`rec-edit-${r.id}`} onClick={() => setEdit({ ...r, directions: r.directions?.length ? r.directions : [""] })}
                    className="text-[11px] font-semibold text-[#C86235] hover:underline">Επεξεργασία</button>
                </div>
              )}
            </div>
          ))}
          {(data?.rows || []).length === 0 && (
            <div className="col-span-full rounded-2xl bg-white border border-[#E3DED4] p-10 text-center text-[#6F6A60]">
              Δεν υπάρχουν συνταγές ακόμη.
            </div>
          )}
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="rec-view-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl mt-8 border border-[#E3DED4] overflow-hidden">
            {view.photo_file_id && <img src={fileUrl(view.photo_file_id)} alt={view.name} className="w-full h-56 object-cover" />}
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-2xl text-[#45423C]">{view.name}</h3>
                  <p className="text-xs text-[#6F6A60] mt-1">{view.category} · {view.portions} μερίδες{view.prep_minutes ? ` · ${view.prep_minutes}′` : ""}</p>
                </div>
                <button data-testid="rec-view-close" onClick={() => setView(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
              </div>
              <CostBadge costing={view.costing} canSeeCost={canSeeCost} />
              <div>
                <h4 className="text-[10px] uppercase tracking-widest text-[#6F6A60] font-bold">Υλικά</h4>
                <ul className="mt-2 space-y-1 text-sm">
                  {((view.costing?.items) || view.items || []).map((i, ix) => (
                    <li key={`${i.name}-${ix}`} className="flex justify-between border-b border-[#F1EDE6] pb-1">
                      <span className="text-[#45423C]">{i.name}{i.note ? ` — ${i.note}` : ""}</span>
                      <span className="text-[#6F6A60] tabular">
                        {i.qty} {i.unit}{canSeeCost && i.cost ? ` · ${fmtEuroCents(i.cost)}` : ""}
                        {i.issue ? ` ⚠ ${i.issue}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {(view.directions || []).length > 0 && (
                <div>
                  <h4 className="text-[10px] uppercase tracking-widest text-[#6F6A60] font-bold">Εκτέλεση</h4>
                  <ol className="mt-2 space-y-2 text-sm list-decimal pl-5">
                    {view.directions.map((d, ix) => <li key={`${ix}-${d.slice(0, 12)}`} className="text-[#45423C]">{d}</li>)}
                  </ol>
                </div>
              )}
              {view.notes && <p className="text-sm text-[#6F6A60] italic">{view.notes}</p>}
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="rec-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">{edit.id ? "Επεξεργασία συνταγής" : "Νέα συνταγή"}</h3>
              <button data-testid="rec-close" onClick={() => setEdit(null)} className="p-1.5 rounded-lg hover:bg-[#F1EDE6]"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα πιάτου</label>
                  <input data-testid="rec-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={field} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Κατηγορία</label>
                  <select data-testid="rec-category" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className={field}>
                    {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Μερίδες</label>
                  <input data-testid="rec-portions" type="number" value={edit.portions} onChange={(e) => setEdit({ ...edit, portions: e.target.value })} className={`${field} text-right tabular`} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Τιμή μενού (€)</label>
                  <input data-testid="rec-price" type="number" step="0.5" value={edit.menu_price} onChange={(e) => setEdit({ ...edit, menu_price: e.target.value })} className={`${field} text-right tabular`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Χρόνος (λεπτά)</label>
                  <input data-testid="rec-minutes" type="number" value={edit.prep_minutes} onChange={(e) => setEdit({ ...edit, prep_minutes: e.target.value })} className={`${field} text-right tabular`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Φωτογραφία</label>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="rec-photo-file"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto.mutate(f); e.target.value = ""; }} />
                  <button data-testid="rec-photo-upload" onClick={() => photoRef.current?.click()} disabled={uploadPhoto.isPending}
                    className="mt-1 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A] disabled:opacity-50">
                    <Upload className="h-4 w-4" /> {uploadPhoto.isPending ? "Ανέβασμα…" : edit.photo_file_id ? "Αλλαγή φωτό" : "Ανέβασμα φωτό"}
                  </button>
                </div>
              </div>

              {edit.photo_file_id && (
                <img src={fileUrl(edit.photo_file_id)} alt="preview" data-testid="rec-photo-preview" className="h-32 w-full object-cover rounded-xl" />
              )}

              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] uppercase tracking-widest text-[#6F6A60] font-bold">Υλικά & ποσότητες</h4>
                  <button data-testid="rec-add-item" onClick={() => setEdit({ ...edit, items: [...(edit.items || []), { ingredient_id: "", qty: "", unit: "gr", note: "" }] })}
                    className="text-xs font-semibold text-[#C86235] hover:underline">+ Προσθήκη υλικού</button>
                </div>
                <div className="mt-2 space-y-2">
                  {(edit.items || []).map((it, ix) => (
                    <div key={`item-${ix}`} className="grid grid-cols-12 gap-2 items-center">
                      <select data-testid={`rec-item-${ix}`} value={it.ingredient_id}
                        onChange={(e) => { const items = [...edit.items]; items[ix] = { ...it, ingredient_id: e.target.value }; setEdit({ ...edit, items }); }}
                        className="col-span-5 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm outline-none focus:border-[#45423C]">
                        <option value="">— υλικό —</option>
                        {(ings?.rows || []).map((i) => <option key={i.id} value={i.id}>{i.name} ({fmtEuroCents(i.price_per_unit || 0)}/{i.unit})</option>)}
                      </select>
                      <input data-testid={`rec-qty-${ix}`} type="number" value={it.qty} placeholder="ποσότητα"
                        onChange={(e) => { const items = [...edit.items]; items[ix] = { ...it, qty: e.target.value }; setEdit({ ...edit, items }); }}
                        className="col-span-2 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-right text-sm tabular outline-none focus:border-[#45423C]" />
                      <select data-testid={`rec-unit-${ix}`} value={it.unit}
                        onChange={(e) => { const items = [...edit.items]; items[ix] = { ...it, unit: e.target.value }; setEdit({ ...edit, items }); }}
                        className="col-span-2 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm outline-none focus:border-[#45423C]">
                        {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <input data-testid={`rec-note-${ix}`} value={it.note || ""} placeholder="σημείωση"
                        onChange={(e) => { const items = [...edit.items]; items[ix] = { ...it, note: e.target.value }; setEdit({ ...edit, items }); }}
                        className="col-span-2 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm outline-none focus:border-[#45423C]" />
                      <button data-testid={`rec-del-item-${ix}`} onClick={() => setEdit({ ...edit, items: edit.items.filter((_, i) => i !== ix) })}
                        className="col-span-1 p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {(edit.items || []).length === 0 && <p className="text-sm text-[#6F6A60]">Δεν έχεις προσθέσει υλικά.</p>}
                </div>
              </div>

              <div className="rounded-xl bg-[#F7F5F1] p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="rec-cost-live">
                {(() => { const c = liveCost(edit); return [
                  ["Κόστος συνταγής", fmtEuroCents(c.total)],
                  ["Κόστος / μερίδα", fmtEuroCents(c.per)],
                  ["Food cost", c.fc === null ? "—" : `${c.fc}%`],
                  ["Προτ. τιμή (30%)", fmtEuroCents(c.suggested)],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
                    <div className="font-semibold text-[#45423C] tabular mt-0.5">{v}</div>
                  </div>
                )); })()}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] uppercase tracking-widest text-[#6F6A60] font-bold">Οδηγίες εκτέλεσης</h4>
                  <button data-testid="rec-add-step" onClick={() => setEdit({ ...edit, directions: [...(edit.directions || []), ""] })}
                    className="text-xs font-semibold text-[#C86235] hover:underline">+ Βήμα</button>
                </div>
                <div className="mt-2 space-y-2">
                  {(edit.directions || []).map((d, ix) => (
                    <div key={`step-${ix}`} className="flex gap-2 items-start">
                      <span className="mt-2 text-xs font-mono text-[#6F6A60] w-5">{ix + 1}.</span>
                      <textarea data-testid={`rec-step-${ix}`} value={d} rows={2}
                        onChange={(e) => { const dirs = [...edit.directions]; dirs[ix] = e.target.value; setEdit({ ...edit, directions: dirs }); }}
                        className="flex-1 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
                      <button data-testid={`rec-del-step-${ix}`} onClick={() => setEdit({ ...edit, directions: edit.directions.filter((_, i) => i !== ix) })}
                        className="mt-1 p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Σημειώσεις</label>
                <textarea data-testid="rec-notes" rows={2} value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={field} />
              </div>

              <div className="flex justify-between pt-1">
                {edit.id ? (
                  <button data-testid="rec-delete" onClick={() => { if (window.confirm(`Διαγραφή της συνταγής «${edit.name}»;`)) remove.mutate(edit.id); }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                    <Trash2 className="h-4 w-4" /> Διαγραφή
                  </button>
                ) : <span />}
                <button data-testid="rec-save" onClick={() => save.mutate(edit)} disabled={!edit.name?.trim() || save.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                  <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
